import { BreadcrumbSection } from '@repo/ui';

const KIT_ID_REGEX =
  /\b(?:[1-2]\d{3}[ABC]\d{3}|[A-Z]{3}\d{3}(?:-\d+)?(?:MGI|A)?|KIT\d{3,5}|TESTKIT-\d{4}-\d{4}-\d{4}-\d{12}|temp_kit_id_\d+|TST\d{3,4}|SG\d{2}|CT\d{6}|KT\d{8}|[a-f0-9]{27}|UHYH\d{12}|YHDH\d{12}|unused_kit_\d+_\w+|KT_\d{13})\b/i;

interface RoutePattern {
  pattern: string | ((segment: string) => boolean);

  label: string | ((match: RouteMatch) => string);

  buildHref: (match: RouteMatch, segments: string[]) => string;

  when?: (match: RouteMatch, segments: string[]) => boolean;

  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
}

interface RouteMatch {
  params: Record<string, string>;
  segment: string;
  index: number;
  overridenLabel?: string;
}

const ROUTE_PATTERNS: RoutePattern[] = [
  {
    pattern: 'results',
    label: 'Results',
    buildHref: ({ params }) => `/results/${params.kitId}`,
  },
  {
    pattern: 'insights',
    label: 'Insights',
    buildHref: ({ params }) => `/results/${params.kitId}/insights/all-insights`,
    when: ({ index }, segments) =>
      !isNaN(+segments[index + 2]) && segments[index + 1] !== 'all-insights',
  },
  {
    pattern: 'all-insights',
    label: ({ overridenLabel }) => overridenLabel ?? 'Insights',
    buildHref: ({ params }) => `/results/${params.kitId}/insights/all-insights`,
  },
  {
    pattern: 'all-metrics',
    label: ({ overridenLabel }) => overridenLabel ?? 'All Metrics',
    buildHref: ({ params }) => `/results/${params.kitId}/insights/all-metrics`,
  },
  {
    pattern: s => !isNaN(+s),
    label: ({ overridenLabel, segment }) =>
      overridenLabel ?? `Metric #${segment}`,
    buildHref: ({ params, segment, index }, segments) =>
      `/results/${params.kitId}/insights/${segments[index - 1]}/${segment}`,
    when: ({ index }, segments) =>
      segments[index - 1] !== 'all-insights' &&
      segments[index - 2] === 'insights',
  },
  {
    pattern: s => !isNaN(+s),
    label: ({ overridenLabel, segment }) =>
      overridenLabel ?? `Metric #${segment}`,
    buildHref: ({ params, segment }) =>
      `/results/${params.kitId}/insights/all-insights/${segment}`,
    when: ({ index }, segments) => segments[index - 1] === 'all-insights',
  },
  {
    pattern: 'conditions',
    label: ({ overridenLabel }) => overridenLabel ?? 'Health associations',
    buildHref: ({ params }) => `/results/${params.kitId}/conditions/metrics`,
    when: ({ index }, segments) =>
      segments[index + 1] === 'metrics' ||
      segments[index + 1] === 'emerging-research' ||
      !isNaN(+segments[index + 1]),
  },
  {
    pattern: s => !!s,
    label: 'Emerging Research',
    buildHref: ({ params }) =>
      `/results/${params.kitId}/conditions/emerging-research`,
    when: ({ index, segment }, segments) =>
      segment === 'emerging-research' ||
      (segments[index - 1] === 'conditions' && !isNaN(+segment)),
  },
  {
    pattern: s => !!s,
    label: ({ overridenLabel, segment }) => overridenLabel ?? segment,
    buildHref: ({ params, segment }) =>
      `/results/${params.kitId}/conditions/${segment}`,
    when: ({ index, segment }, segments) =>
      segments[index - 1] === 'conditions' && !isNaN(+segment),
  },
  {
    pattern: s => !!s,
    label: ({ segment, overridenLabel }) =>
      overridenLabel ?? `Condition #${segment}`,
    buildHref: ({ params, segment }) =>
      `/results/${params.kitId}/conditions/metrics/${segment}`,
    when: ({ index }, segments) =>
      segments[index - 2] === 'conditions' && segments[index - 1] === 'metrics',
  },
  {
    pattern: 'seeding',
    label: 'Microbiome Seeding',
    buildHref: ({ params }) => `/results/${params.kitId}/seeding`,
  },
  {
    pattern: 'microbiome',
    label: 'Microbiome Breakdown',
    buildHref: ({ params }) => `/results/${params.kitId}/microbiome`,
  },
];

function isKitId(segment: string): boolean {
  return KIT_ID_REGEX.test(segment);
}

function matchesPattern(
  pattern: string | ((segment: string) => boolean),
  segment: string,
): boolean {
  if (typeof pattern === 'function') {
    return pattern(segment);
  }
  return pattern === segment;
}

export default function breadcrumbResultsPortalParser({
  overrides,
}: {
  overrides?: Record<string, string | undefined>;
}): BreadcrumbSection[] {
  const location = window?.location?.pathname ?? '';
  const urlSegments = location.split('/').filter(Boolean);

  if (urlSegments.length === 0) return [];

  const params: Record<string, string> = {};

  const kitIdIndex = urlSegments.findIndex(isKitId);
  if (kitIdIndex !== -1) {
    params.kitId = urlSegments[kitIdIndex];
  }

  const sections: BreadcrumbSection[] = [];

  urlSegments.forEach((segment, index) => {
    for (const route of ROUTE_PATTERNS) {
      if (!matchesPattern(route.pattern, segment)) {
        continue;
      }

      const overridenLabel = overrides?.[segment];

      const match: RouteMatch = { params, segment, index, overridenLabel };

      if (route.when && !route.when(match, urlSegments)) {
        continue;
      }

      const label =
        typeof route.label === 'function' ? route.label(match) : route.label;

      const href = route.buildHref(match, urlSegments);

      sections.push({ label, href, icon: route.icon });
    }
  });

  return sections;
}
